<template>
    <div :class="`report-item ${report.status === 'CASE_ARCHIVED' ? 'report-item--archived' : ''}`" :data-id="report.id">
        <div class="report-item__column report-item__column--case">
            <div class="report-item__status">
                <router-link :to="`/zgloszenie/${report.id}`">
                    <div :class="`report-item__status-icon ${ report.case.isReporter ? 'report-item__status-icon--my' : '' }`">
                        <IconFileSearch class="report-item__icon" alt=""/>
                    </div>
                </router-link>
            </div>
            <div class="report-item__details">
                <div class="report-item__number">{{ report.case.number }}</div>
                <div class="report-item__gender">
                    <IconVenus v-if="report.patient.gender === 'K'" class="report-item__gender-icon" />
                    <IconMars v-else class="report-item__gender-icon" />
                    <span>{{ report.patient.gender }}</span>
                </div>
                <div class="report-item__age">WIEK: {{ report.patient.age }}</div>
            </div>
        </div>

        <div class="report-item__column report-item__column--pwz">{{ report.doctor.pwz }}</div>

        <div class="report-item__column report-item__column--date">
            <div class="report-item__date">{{ formatDate(report.case.createdAt) }}</div>
            <div :class="`report-item__relative-time report-item__relative-time--${relativeDate(report.case.createdAt)}`">
                <span><b>{{ translatedRelativeDate(report.case.createdAt) }}</b> temu</span>
            </div>
        </div>

        <div class="report-item__column report-item__column--chat">
            <button @click="onShowMessages" class="report-item__button">
                <IconComment /> czat
            </button>
        </div>

        <div class="report-item__column report-item__column--surgery">
            <div :class="`report-item__surgery report-item__surgery--${report.case.surgicalEvaluation.code || '0'}`" v-if="report.case.surgicalEvaluation !== null">
                {{ getSurgicalEvaluationLabel(report.case.surgicalEvaluation.code) }}
                <ReportListTooltip :show="report.case.surgicalEvaluation.code === 4">
                    {{ report.case.surgicalEvaluation.note ? report.case.surgicalEvaluation.note : 'Brak opisu' }}
                </ReportListTooltip>
            </div>
        </div>

        <div class="report-item__column report-item__column--forecast">
            <div class="report-item__forecast" v-if="report.case.expectedSurgeryDate !== null">
                {{ formatDate(report.case.expectedSurgeryDate) }}
            </div>
            <div class="report-item__weeks" v-if="report.case.expectedSurgeryDate">
                {{ getWeek(report.case.expectedSurgeryDate) }}
            </div>
        </div>

        <div class="report-item__column report-item__column--rating">
            {{ getRatingLabel(report.case.chemotherapyEffectiveness) }}
        </div>

        <div class="report-item__column report-item__column--last">
            <div class="report-item__date" v-if="report.case.lastChemotherapyCycleAt !== null">
                {{ formatDate(report.case.lastChemotherapyCycleAt) }}
            </div>
            <div
                :class="`report-item__relative-time report-item__relative-time--${relativeDate(report.case.lastChemotherapyCycleAt)}`"
                v-if="report.case.lastChemotherapyCycleAt !== null"
            >
                <span><b>{{ translatedRelativeDate(report.case.lastChemotherapyCycleAt) }}</b> temu</span>
            </div>
        </div>

        <div class="report-item__column report-item__column--risk">
            <div :class="`report-item__risk report-item__risk--${report.case.procedureRisk.code || 0}`">
                <div class="report-item__risk-label">{{ getRadicalLabel(report.case.procedureRisk.code) }}</div>

                <ReportListTooltip :show="report.case.procedureRisk.code === 2">
                    {{ report.case.procedureRisk.note ? report.case.procedureRisk.note : 'Brak opisu' }}
                </ReportListTooltip>
            </div>
        </div>

        <div class="report-item__column report-item__column--links" v-if="userData.is_consultant || userData.is_admin">
            <button @click="changeIntoEditState" class="report-item__button">
                <IconInlineEdit /> opis
            </button>
            <button @click="onEdit" class="report-item__button">
                <IconEdit /> edycja
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { IconEdit, IconInlineEdit, IconFileSearch, IconVenus, IconMars, IconComment } from '@/components/ui/icons'
import ReportListTooltip from "./ReportListTooltip.vue"
import utils from "./ReportUtils"
import { surgicalEvaluation, chemotherapyEffectiveness, procedureRisk } from "./ReportCodeTranslations.js"

export default {
    name: "ReportListItem",
    components: {
        IconEdit,
        IconInlineEdit,
        IconFileSearch,
        IconVenus,
        IconMars,
        IconComment,
        ReportListTooltip,
    },
    computed: mapGetters({
        userData: "getUserData",
    }),
    props: {
        report: {
            type: Object,
            required: true,
        },
    },

    methods: {
        ...utils,

        changeIntoEditState() {
            this.report.originalStatus = this.report.status;
            this.report.status = 'CASE_EDIT';
        },

        // get surgical evaluation label from object by code
        getSurgicalEvaluationLabel(code) {
            return code ? surgicalEvaluation[code] : 'Brak';
        },

        // get rating label from object by code
        getRatingLabel(code) {
            return code ? chemotherapyEffectiveness[code] : 'Brak oceny';
        },

        // get radical label from object by code
        getRadicalLabel(code) {
            return code ? procedureRisk[code] : '-';
        },

        onShowMessages() {
            this.$store.commit("saveReportTableData", {latestOpenedReportId: this.report.id} );
            this.$router.push(`/zgloszenie/${this.report.id}/wiadomosci`);
        },
        onEdit() {
            this.$store.commit("saveReportTableData", {latestOpenedReportId: this.report.id} );
            this.$router.push(`/zgloszenie/${this.report.id}/konsultacja`);
        },
    }
};
</script>

<style lang="scss">
.report-item {
    display: grid;
    align-items: center;
    grid-template-areas: "case pwz date chat surgery forecast rating last risk links";
    grid-template-columns: 12% 6% 11% 4% 14% 10% 10% 10% 10%;
    grid-gap: 8px;
    padding: 0 15px;
    border-bottom: 1px solid #f4f4f4;
    scroll-margin-top: 200px;

    &--archived {
        background: rgba(0,0,0,0.07);
        color: #B6B6B6;

        .report-item__relative-time:before {
            background: #B6B6B6 !important;
        }

        .report-item__surgery--1 {
            background: #B6B6B6;
        }
    }

    &__column {
        padding: 15px 0;

        &--inline {
            padding: 0;
        }
        &--edit {
            padding: 0;
        }
        &--links {
            display: flex;
            justify-content: space-between;
            padding: 0;
            gap: 8px;
        }
    }

    &__column--case {
        grid-area: case;
        display: flex;
        align-items: center;
    }

    &__icon {
        width: 14px;
        height: 18px;
        color: white;
    }

    &__status {
        &-icon {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background: #c4c4c4;
            margin-right: 12px;
            display: flex;
            align-items: center;
            justify-content: center;

            &--my {
                background: #46C0CF;
            }
        }
    }

    &__details {
        display: flex;
        flex-wrap: wrap;
        color: #b6b6b6;
        font-size: 12px;
    }

    &__number {
        width: 100%;
        color: #4a4949;
        font-size: 14px;
    }

    &__gender {
        display: flex;
        margin-right: 10px;

        &-icon {
            width: 8px;
            margin-right: 2px;
            color: gray;
        }
    }

    &__surgery {
        background: gray;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
        padding: 5px 15px;
        min-height: 35px;
        font-size: 12px;
        font-weight: 600;

        &--1 { background-color: #8dc643; text-align: center; }
        &--2 { background-color: #f89f1d; text-align: center; }
        &--3 { background-color: #e82421; text-align: center; }
        &--4 { background-color: #46c0cf; justify-content: space-between; }
    }

    &__relative-time {
        font-size: 12px;

        span {
            color: #b6b6b6;
        }

        &:before {
            content: '';
            display: inline-block;
            width: 10px;
            height: 10px;
            border-radius: 10px;
            margin-right: 6px;
            background: #E82421;
        }

        &--0:before { background: #B6B6B6 }
        &--1:before { background: #8DC643 }
        &--2:before { background: #00A650 }
        &--3:before { background: #00A99E }
        &--4:before { background: #0460AD }
        &--5:before { background: #5B56A6 }
        &--6:before { background: #8F55A2 }
        &--7:before { background: #CD188D }
        &--8:before { background: #F89F1D }
        &--9:before { background: #F47820 }
        &--10:before { background: #F05E26 }
    }

    &__weeks {
        font-size: 12px;
        color: #b6b6b6;
    }

    &__risk {
        display: flex;
        align-items: center;

        &:before {
            content: '';
            display: inline-block;
            width: 10px;
            height: 10px;
            border-radius: 10px;
            margin-right: 6px;
            background: #b6b6b6;
        }

        &--1:before { background: #8dc643; }
        &--2:before { background: #FFD600; }
    }

    &__button {
        cursor: pointer;
        color: #999999;
        font-size: 14px;
        display: flex;
        flex-direction: column;
        width: 100%;
        align-items: center;
        padding: 5px;
        background: transparent;
        border: 1px solid #fff;
        margin: 5px 0;
        border-radius: 4px;
        box-sizing: border-box;
        font-family: "Poppins", sans-serif;
        line-height: 1;

        svg {
            margin-bottom: 5px;
        }
    }

}
</style>
